export function addLocalDays(localDate: string, days: number): string {
  const date = dateValue(localDate);
  date.setUTCDate(date.getUTCDate() + days);
  return isoDate(date);
}

export function formatRangeLabel(startDate: string, endDate: string): string {
  const start = dateParts(startDate);
  const end = dateParts(endDate);
  const startMonth = monthLabel(startDate);
  const endMonth = monthLabel(endDate);
  if (start.year === end.year && start.month === end.month) {
    return `${startMonth} ${start.day}–${end.day}, ${end.year}`;
  }
  if (start.year === end.year) {
    return `${startMonth} ${start.day}–${endMonth} ${end.day}, ${end.year}`;
  }
  return `${startMonth} ${start.day}, ${start.year}–${endMonth} ${end.day}, ${end.year}`;
}

export function formatDayName(localDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: "UTC",
  }).format(dateValue(localDate));
}

export function formatDayNumber(localDate: string): string {
  return dateParts(localDate).day.toString();
}

export function formatFullDate(localDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(dateValue(localDate));
}

export function formatShortOrdinalDate(localDate: string): string {
  const { day, year } = dateParts(localDate);
  const weekday = formatDayName(localDate);
  const month = monthLabel(localDate);
  return `${weekday}, ${month} ${day.toString()}${ordinalSuffix(day)}, ${year.toString()}`;
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

function monthLabel(localDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    timeZone: "UTC",
  }).format(dateValue(localDate));
}

function dateValue(localDate: string): Date {
  const { year, month, day } = dateParts(localDate);
  return new Date(Date.UTC(year, month - 1, day));
}

function ordinalSuffix(day: number): "st" | "nd" | "rd" | "th" {
  const lastTwoDigits = day % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return "th";
  if (day % 10 === 1) return "st";
  if (day % 10 === 2) return "nd";
  if (day % 10 === 3) return "rd";
  return "th";
}

function dateParts(localDate: string): { year: number; month: number; day: number } {
  const [year, month, day] = localDate.split("-").map(Number);
  if (!year || !month || !day) throw new Error("A valid local date is required.");
  return { year, month, day };
}

function isoDate(date: Date): string {
  const year = date.getUTCFullYear().toString().padStart(4, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}
